import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventNoteIcon from "@mui/icons-material/EventNote";

import { StyledCard } from "../common/StyledCard";
import { FeeStructureResponseDto } from "../../shared/dtos/feeStructure/FeeStructureResponseDto";
import { formatCurrency } from "../../utils/currencyUtils";

interface FeeStructureCardProps {
  feeStructure: FeeStructureResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const FeeStructureCard: React.FC<FeeStructureCardProps> = ({
  feeStructure,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, flexWrap: "nowrap" }}
            >
              {feeStructure.voteHeadName}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "secondary.main", fontWeight: 700 }}
            >
              {formatCurrency(feeStructure.amount)}
            </Typography>
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Details */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <SchoolIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Grade:</b> {feeStructure.gradeLevelName}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CalendarTodayIcon
              fontSize="small"
              sx={{ color: "action.active" }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Year:</b> {feeStructure.academicYearName}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <EventNoteIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Term:</b> {feeStructure.termName}
            </Typography>
          </Stack>
        </Stack>

        {/* Branch Footer */}
        {feeStructure.branchName && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={feeStructure.branchName}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default FeeStructureCard;
